'use client'

// WorldClock — shows the world's time of day in the HUD. The sim broadcasts
// dayPhase (0 = midnight, 0.25 dawn, 0.5 noon, 0.75 dusk) in the environment;
// we map it to a 24h HH:MM. Shown in both camera and first-person views.

import useGameStore from '../../stores/gameStore'
import { ui, kawaii } from '../../lib/ui/theme'
import { hud } from '../../lib/ui/layout'
import { initialTrip } from '../three/effects/PostFX'

// Sam's mushroom (Novosel), eyes removed so the stem is blank: the badge
// is the world telling you his head is elsewhere, and a face on it would
// be a second character. The two trailing subpaths of the original were
// the eyes; they simply end before them.
const MUSHROOM_D = 'm81.544 23.107c-2.4576 0.56738-2.6033 8.4226-0.4885 17.583 2.1148 9.1602 5.6892 16.157 8.1468 15.589 2.4576-0.56738 4.0039-7.3591 1.8891-16.519-2.114-9.161-7.089-17.221-9.547-16.653zm-74.732 45.567c-4.3819-5.246-5.8125-12.508-5.8125-21.598 0-13.988 5.8444-25.628 15.361-34.294 8.271-7.5316 19.351-11.782 31.346-11.782 14.072 0 24.859 4.3296 33.422 14.307 7.1343 8.3123 13.286 19.887 13.286 31.769 0 8.6494-1.4001 15.199-5.3973 20.352-3.2806 4.2293-7.8582 7.4188-13.293 9.7161-0.06661 4.9925-0.69044 10.405-6.2288 13.534-4.8461 2.7376-15.619 3.3214-22.451 3.3214-8.8851 0-17.766 0.13762-22.867-4.1518-3.277-2.754-4.646-8.417-4.932-12.512-5.015-2.058-9.284-4.89-12.434-8.661zm40.687-16.192c12.382 0 22.42-10.038 22.42-22.42s-10.038-22.42-22.42-22.42-22.42 10.038-22.42 22.42 10.038 22.42 22.42 22.42zm-41.403 2.67c2.4363 0.65281 6.2527-6.2145 8.6859-15.295 2.4332-9.0808 2.5617-16.936 0.12543-17.589-2.436-0.654-6.9895 4.617-9.4227 13.698s-1.8244 18.533 0.6119 19.186zm19.399 31.665c4.4687 2.9331 13.044 3.4463 21.174 3.4463 9.0167 0 19.014-0.03174 23.665-3.9387 2.741-2.3027 2.9062-8.7784 2.9062-11.753 0-1.2379-0.32761-3.0819-1.2455-4.2369-1.9638-2.4711-5.7605-4.0166-8.7187-5.3973-4.6188-2.1557-8.1814-2.4911-15.569-2.4911-6.813 0-11.988 0.65606-16.815 2.4911-3.8707 1.4716-8.5761 3.7606-9.9642 6.2276-0.52197 0.92768 0 2.2567 0 3.4066 0 3.4705 1.159 10.008 4.5669 12.245z'

// Visible while he is up: the same envelope the smear follows, plus the
// ?trip preview override so the look can be judged without a live trip.
function TripBadge() {
  const level = useGameStore((s) => s.environment?.tripLevel || 0)
  const shown = Math.max(level, initialTrip() ?? 0)
  if (shown < 0.05) return null
  return (
    <div
      aria-label="tripping"
      style={{
        width: 42,
        height: 42,
        borderRadius: '50%',
        border: kawaii.outline,
        boxShadow: kawaii.dropSm,
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
        opacity: Math.min(1, shown * 2),
        transition: 'opacity 600ms ease',
      }}
    >
      {/* the gradient is a bigger disc spinning behind the cut-out, which
          is the cheapest trippy there is: one transform, no repaints */}
      <div
        className="animate-spin"
        style={{
          position: 'absolute',
          inset: -14,
          animationDuration: '7s',
          background: 'conic-gradient(from 0deg, #34d399, #7c5cff, #a78bfa, #4ade80, #6ee7b7, #8b5cf6, #34d399)',
        }}
      />
      {/* White, to read against the gradient. The drop shadow is not
          decoration: the conic spins, so pale greens pass behind the cap
          and white alone would wash out for part of every turn. */}
      <svg viewBox="0 0 95.41467 95" width="28" height="28" aria-hidden="true"
        style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          filter: 'drop-shadow(0 1px 1.5px rgba(29,22,58,0.55))',
        }}>
        <path d={MUSHROOM_D} fill="#ffffff" />
      </svg>
    </div>
  )
}

function formatClock(dayPhase) {
  const p = ((dayPhase % 1) + 1) % 1
  const totalMin = Math.round(p * 24 * 60) % (24 * 60)
  const hh = Math.floor(totalMin / 60)
  const mm = totalMin % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

export function WorldClock() {
  // Select the FORMATTED string: environment updates arrive ~5Hz, but the
  // clock only changes once per world-minute, so string equality skips the
  // other ~299 re-renders a minute.
  const clock = useGameStore((s) =>
    s.environment?.dayPhase == null ? null : formatClock(s.environment.dayPhase)
  )
  if (clock == null) return null

  // Night gets a moon, day gets a sun. The clock is the one dark pill in the
  // set on purpose: it's the sky's colour, not the HUD's.
  const hour = Number(clock.slice(0, 2))
  const isNight = hour < 6 || hour >= 19

  return (
    // Mobile: band two, left side. The old top-14 right-4 sat the clock ON
    // the needs card (the card is ~66px tall at 375px) and then fought the
    // connect pill for the same corner. World info left, account right.
    <>
    <div className="fixed top-[74px] left-3 right-auto md:top-4 md:right-6 md:left-auto z-50 pointer-events-none select-none flex items-center gap-2">
      <div className="hidden md:block"><TripBadge /></div>
      <div
        className="flex items-center gap-2"
        style={{
          padding: '7px 15px 7px 12px',
          borderRadius: kawaii.radiusPill,
          background: isNight ? kawaii.night : '#6fb7d8',
          border: kawaii.outline,
          boxShadow: kawaii.dropSm,
          color: '#fdf6ea',
          fontFamily: ui.font,
          fontVariantNumeric: 'tabular-nums',
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: '0.02em',
        }}
      >
        {isNight ? <MoonMark /> : <SunMark />}
        {clock}
      </div>
    </div>
    {/* On a phone the mushroom sits bottom left, just above the status
        pill (Sam, 29 Sep), so band 2 left is the clock alone and the
        folded first line can sit under it. It only shows, it takes no
        touches, so the walking stick still starts under it. */}
    <div
      className="md:hidden fixed left-3 z-40 pointer-events-none select-none"
      style={{ bottom: `calc(${hud.tripBottomMobile}px + env(safe-area-inset-bottom, 0px))` }}
    >
      <TripBadge />
    </div>
    </>
  )
}

function MoonMark() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z"
        fill="#ffe9a8"
        stroke="#2f2650"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function SunMark() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      <circle cx="12" cy="12" r="5.4" fill="#ffe08a" stroke="#2f2650" strokeWidth="2" />
      <g stroke="#2f2650" strokeWidth="2" strokeLinecap="round">
        <path d="M12 1.6v2.4M12 20v2.4M1.6 12h2.4M20 12h2.4M4.6 4.6l1.7 1.7M17.7 17.7l1.7 1.7M19.4 4.6l-1.7 1.7M6.3 17.7l-1.7 1.7" />
      </g>
    </svg>
  )
}

export default WorldClock
